/**
 * Text and email templates (ACL-385, ADR-111; ACL-378, ADR-198 in platform): the words a workflow
 * texts or emails a lead, written once in English and, when the admin wants, in Spanish. A template
 * is a text's or an email's for good (`channel`); an email one has a subject in each language. The lead's language picks the text; with no
 * Spanish text, English goes. A text names a lead's details as fields, `{{first_name}}`, each with
 * an optional word for when the lead has none: `{{first_name|there}}`.
 *
 * Everything here is pure, so the editor says what is wrong and shows the text as a lead would get
 * it before anything is sent. core-api has the last word: it checks the same things on save, and
 * fills and sends the text (`messages.body_to_send` adds the opt-out line under a first text).
 */

export type TemplateLanguage = "en" | "es";
/** What a template is written for. Absent from an older core-api: a text. */
export type TemplateChannel = "sms" | "email";

export const LANGUAGES: readonly TemplateLanguage[] = ["en", "es"];
export const LANGUAGE_NAME: Record<TemplateLanguage, string> = { en: "English", es: "Spanish" };

/** A workflow that names the template, and whether it is live: a published one keeps it on. */
export interface TemplateUse {
  id: string;
  name: string;
  status: "draft" | "published" | "archived";
}

/** A template as core-api's `/message-templates` answers it: one per slug, its texts by language. */
export interface TextTemplate {
  slug: string;
  name: string;
  active: boolean;
  texts: { en: string; es: string | null };
  used_by: TemplateUse[];
  updated_at: string;
  updated_by_name: string | null;
  channel?: TemplateChannel;
  /** An email template's subjects; null on a text template. */
  subjects?: { en: string | null; es: string | null } | null;
}

/** What the editor sends. `updated_at` is the copy it read, so a save over a newer one is a 409.
 *  `channel` only when the template is made: it stays what it was made. */
export interface TextTemplateInput {
  name: string;
  texts: { en: string; es: string | null };
  updated_at?: string;
  channel?: TemplateChannel;
  subjects?: { en: string | null; es: string | null };
}

export function channelOf(template: Pick<TextTemplate, "channel">): TemplateChannel {
  return template.channel ?? "sms";
}

/** A lead detail a text may name, with the value the preview fills in, and the most characters a
 *  lead's value is given when a text is checked for length (`message_text.FIELDS` in platform). */
export interface MergeField {
  key: string;
  label: string;
  sample: string;
  longest?: number;
}

/** The fields core-api fills (ADR-111). The catalog lists them too; these stand in until it does. */
export const MERGE_FIELDS: readonly MergeField[] = [
  { key: "first_name", label: "First name", sample: "Maria", longest: 40 },
  { key: "last_name", label: "Last name", sample: "Garcia", longest: 40 },
  { key: "firm_name", label: "Firm name", sample: "Larry H. Parker", longest: 200 },
];

/** As `messages.MAX_SEND_CHARS`: a text, with its opt-out line, is at most this long. */
export const MAX_TEXT_CHARS = 1600;
export const MAX_NAME_CHARS = 80;

/** The line under the first text to a number, in the text's language. The English one is
 *  `messages.OPT_OUT_LINE`, word for word. */
export const OPT_OUT_LINE: Record<TemplateLanguage, string> = {
  en: "Reply STOP to stop.",
  es: "Responda STOP para cancelar.",
};

const FIELD = /\{\{([^{}]*)\}\}/g;

/** One field as written in a text: its key, and the word for when the lead has none. */
export interface FieldUse {
  key: string;
  fallback: string | null;
}

/** The fields a text names, in order. `{{ first_name | there }}` reads as first_name, "there". */
export function fieldsIn(text: string): FieldUse[] {
  return [...text.matchAll(FIELD)].map((m) => {
    const inner = m[1] ?? "";
    const bar = inner.indexOf("|");
    return bar === -1
      ? { key: inner.trim(), fallback: null }
      : { key: inner.slice(0, bar).trim(), fallback: inner.slice(bar + 1).trim() };
  });
}

/** Whether a text opens a field and never closes it, or closes one never opened. */
export function hasBrokenField(text: string): boolean {
  const rest = text.replace(FIELD, "");
  return rest.includes("{{") || rest.includes("}}");
}

/** The text with each field filled: the lead's value, else the field's own word, else nothing. */
export function fillText(text: string, values: Readonly<Record<string, string | null | undefined>>): string {
  return text.replace(FIELD, (_whole, inner: string) => {
    const bar = inner.indexOf("|");
    const key = (bar === -1 ? inner : inner.slice(0, bar)).trim();
    const value = values[key]?.trim();
    if (value) return value;
    return bar === -1 ? "" : inner.slice(bar + 1).trim();
  });
}

/** The text filled as long as it can be, as `message_text.longest` fills it: each field its own
 *  most, or its word when that is longer. A field the catalog gives no most is its sample's length. */
export function longestText(text: string, fields: readonly MergeField[]): string {
  const most = new Map(fields.map((f) => [f.key, f.longest ?? f.sample.length]));
  return text.replace(FIELD, (_whole, inner: string) => {
    const bar = inner.indexOf("|");
    const key = (bar === -1 ? inner : inner.slice(0, bar)).trim();
    const word = bar === -1 ? "" : inner.slice(bar + 1).trim();
    return "x".repeat(Math.max(most.get(key) ?? 0, [...word].length));
  });
}

/** Whether a text says how to stop by itself, as `messages.body_to_send` reads it: then no opt-out
 *  line goes under it. */
export function saysHowToStop(text: string): boolean {
  const said = text.toLowerCase();
  return said.includes("reply stop") || said.includes("responda stop");
}

/** A text as it leaves, as `messages.body_to_send` sends it: trimmed, and under a first text the
 *  opt-out line in the text's language, unless the writer already said how to stop. */
export function asSent(text: string, language: TemplateLanguage, first: boolean): string {
  const body = text.trim();
  return first && !saysHowToStop(body) ? `${body}\n${OPT_OUT_LINE[language]}` : body;
}

/** What the preview says under a text shown as a number's first or a later one. Which it is, is the
 *  number's and not the template's (ACL-589): core-api puts the opt-out line under the first text
 *  each number gets from us, whichever template it is (ADR-098 in platform). */
export function optOutNote(text: string, first: boolean): string {
  if (!first) return "Every text after a number's first goes as written, with no opt-out line.";
  if (saysHowToStop(text)) return "Your text says how to stop, so no opt-out line is added to it.";
  return "A number's first text from us always ends with the opt-out line. To word it yourself, write \"Reply STOP\" (or \"Responda STOP\") in the text.";
}

// The GSM-7 alphabet a phone sends at 160 characters a part; the extension characters count twice.
const GSM_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXTENDED = "^{}\\[~]|€\f";

/** How a text goes over SMS: its length as the carrier counts it, and in how many parts. Any
 *  character outside GSM-7 (an emoji, a curly quote) sends the whole text as Unicode, 70 a part. */
export function smsParts(text: string): { characters: number; parts: number; unicode: boolean } {
  let gsm = 0;
  let unicode = false;
  for (const ch of text) {
    if (GSM_BASIC.includes(ch)) gsm += 1;
    else if (GSM_EXTENDED.includes(ch)) gsm += 2;
    else {
      unicode = true;
      break;
    }
  }
  const characters = unicode ? text.length : gsm;
  if (characters === 0) return { characters, parts: 0, unicode };
  const [single, each] = unicode ? [70, 67] : [160, 153];
  return { characters, parts: characters <= single ? 1 : Math.ceil(characters / each), unicode };
}

/** The values the preview fills, from the fields' samples. */
export function sampleValues(fields: readonly MergeField[]): Record<string, string> {
  return Object.fromEntries(fields.map((f) => [f.key, f.sample]));
}

/** Where a problem is, so the editor puts it under that box. */
export type TemplatePlace = "name" | TemplateLanguage | "subject_en" | "subject_es";

/**
 * What stops a template from being kept, as one sentence, and where; null when nothing does, as
 * core-api's `message_templates.problem` and `create` say it. The name and an English text are
 * needed; Spanish is optional. A field must be one the platform fills, written `{{first_name}}`,
 * and a text filled as long as it can be, with the opt-out line under it, fits in 1,600 characters.
 * A new template's name must give it a slug.
 */
export function templateProblem(
  input: TextTemplateInput,
  fields: readonly MergeField[] = MERGE_FIELDS,
  { creating = false }: { creating?: boolean } = {},
): { message: string; place: "name" | TemplateLanguage } | null {
  const name = input.name.trim();
  if (!name) return { message: "Give the template a name.", place: "name" };
  if (name.length > MAX_NAME_CHARS) {
    return { message: `A template's name is at most ${MAX_NAME_CHARS} characters.`, place: "name" };
  }
  if (!input.texts.en.trim()) return { message: "Write the English text.", place: "en" };
  const known = new Set(fields.map((f) => f.key));
  for (const language of LANGUAGES) {
    const text = input.texts[language];
    if (!text || !text.trim()) continue;
    const where = LANGUAGE_NAME[language];
    if (hasBrokenField(text)) {
      return { message: `${where} text: a field is written between {{ and }}, like {{first_name}}.`, place: language };
    }
    const unknown = fieldsIn(text).find((f) => !known.has(f.key));
    if (unknown) {
      const named = unknown.key ? `{{${unknown.key}}}` : "{{}}";
      return {
        message: `${where} text: ${named} is not a detail the platform fills. Use ${[...known].map((k) => `{{${k}}}`).join(", ")}.`,
        place: language,
      };
    }
    if ([...asSent(longestText(text, fields), language, true)].length > MAX_TEXT_CHARS) {
      return {
        message: `${where} text is too long: with the opt-out line it must fit in ${MAX_TEXT_CHARS.toLocaleString("en-US")} characters.`,
        place: language,
      };
    }
  }
  if (creating && !slugFrom(name)) {
    return { message: "Give the template a name with letters or digits in it.", place: "name" };
  }
  return null;
}

// --- email templates (ACL-378) --------------------------------------------------------------------

/** As `emails.MAX_BODY_CHARS` and `emails.MAX_SUBJECT_CHARS` in platform. */
export const MAX_EMAIL_CHARS = 20000;
export const MAX_SUBJECT_CHARS = 255;

/** The line under every automated email, in its language: `emails.STOP_LINES`, word for word. */
export const EMAIL_STOP_LINE: Record<TemplateLanguage, string> = {
  en: "Don't want these emails? Reply STOP.",
  es: "¿No quiere recibir estos correos? Responda STOP.",
};

/** An email's words as they leave, as `emails.with_stop_line` sends them: line endings made one,
 *  trimmed, then a blank line and the STOP line in the email's language. */
export function asEmailSent(text: string, language: TemplateLanguage): string {
  return `${text.replace(/\r\n/g, "\n").trim()}\n\n${EMAIL_STOP_LINE[language]}`;
}

function fieldProblem(text: string, where: string, place: TemplatePlace, fields: readonly MergeField[]) {
  const known = new Set(fields.map((f) => f.key));
  if (hasBrokenField(text)) {
    return { message: `${where}: a field is written between {{ and }}, like {{first_name}}.`, place };
  }
  const unknown = fieldsIn(text).find((f) => !known.has(f.key));
  if (unknown) {
    const named = unknown.key ? `{{${unknown.key}}}` : "{{}}";
    return {
      message: `${where}: ${named} is not a detail the platform fills. Use ${[...known].map((k) => `{{${k}}}`).join(", ")}.`,
      place,
    };
  }
  return null;
}

/**
 * What stops an email template from being kept, as core-api's `message_templates.problem` says it
 * for one: a name, an English subject and email, and for Spanish both or neither; the fields the
 * platform fills; a subject on one line that, filled as long as it can be, fits in 255 characters;
 * and an email that, filled so and with the STOP line under it, fits in 20,000.
 */
export function emailTemplateProblem(
  input: TextTemplateInput,
  fields: readonly MergeField[] = MERGE_FIELDS,
  { creating = false }: { creating?: boolean } = {},
): { message: string; place: TemplatePlace } | null {
  const name = input.name.trim();
  if (!name) return { message: "Give the template a name.", place: "name" };
  if (name.length > MAX_NAME_CHARS) {
    return { message: `A template's name is at most ${MAX_NAME_CHARS} characters.`, place: "name" };
  }
  const subjects = input.subjects ?? { en: null, es: null };
  const written = (value: string | null | undefined) => Boolean(value?.trim());
  if (!written(subjects.en)) return { message: "Write the English subject.", place: "subject_en" };
  if (!written(input.texts.en)) return { message: "Write the English email.", place: "en" };
  if (written(input.texts.es) && !written(subjects.es)) {
    return { message: "Write the Spanish subject, or take the Spanish email out.", place: "subject_es" };
  }
  if (written(subjects.es) && !written(input.texts.es)) {
    return { message: "Write the Spanish email, or take the Spanish subject out.", place: "es" };
  }
  for (const language of LANGUAGES) {
    const subject = subjects[language];
    const text = input.texts[language];
    if (!written(text) || subject == null) continue;
    const where = LANGUAGE_NAME[language];
    const place: TemplatePlace = language === "en" ? "subject_en" : "subject_es";
    const inSubject = fieldProblem(subject, `${where} subject`, place, fields);
    if (inSubject) return inSubject;
    if (/[\r\n]/.test(subject)) return { message: `${where} subject: a subject is one line.`, place };
    if ([...longestText(subject, fields).trim()].length > MAX_SUBJECT_CHARS) {
      return { message: `${where} subject is too long: filled, it must fit in ${MAX_SUBJECT_CHARS} characters.`, place };
    }
    const inText = fieldProblem(text ?? "", `${where} email`, language, fields);
    if (inText) return inText;
    if ([...asEmailSent(longestText(text ?? "", fields), language)].length > MAX_EMAIL_CHARS) {
      return {
        message: `${where} email is too long: with the STOP line it must fit in ${MAX_EMAIL_CHARS.toLocaleString("en-US")} characters.`,
        place: language,
      };
    }
  }
  if (creating && !slugFrom(name)) {
    return { message: "Give the template a name with letters or digits in it.", place: "name" };
  }
  return null;
}

/** What the email editor sends: Spanish left blank is no Spanish email and no Spanish subject. */
export function emailTemplatePayload(input: TextTemplateInput): TextTemplateInput {
  const es = input.texts.es?.trim() ? input.texts.es : null;
  const subjectEs = input.subjects?.es?.trim() ? input.subjects.es.trim() : null;
  return {
    ...input,
    name: input.name.trim(),
    texts: { en: input.texts.en, es },
    subjects: { en: input.subjects?.en?.trim() ?? "", es: subjectEs },
  };
}

/** Whether a template can be turned off: not while a published workflow sends it. */
export function liveUses(template: Pick<TextTemplate, "used_by">): TemplateUse[] {
  return template.used_by.filter((u) => u.status === "published");
}

/** The languages a template has text in, English first. */
export function languagesOf(template: Pick<TextTemplate, "texts">): TemplateLanguage[] {
  return LANGUAGES.filter((l) => Boolean(template.texts[l]?.trim()));
}

/** The language a lead gets: theirs when the template has it, else English. */
export function textFor(template: Pick<TextTemplate, "texts">, language: string | null | undefined): TemplateLanguage {
  return language === "es" && template.texts.es?.trim() ? "es" : "en";
}

/** The values the editor sends: Spanish left blank is no Spanish text. */
export function templatePayload(input: TextTemplateInput): TextTemplateInput {
  const es = input.texts.es?.trim() ? input.texts.es : null;
  return { ...input, name: input.name.trim(), texts: { en: input.texts.en, es } };
}

/** A template's slug, made from its name when it is created and fixed after, as a disposition's is:
 *  a workflow names the template by it. Lowercase letters, digits and hyphens, at most 60. */
export function slugFrom(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
}
